import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LIST_VISIBILITIES, LIST_VISIBILITY_LABEL, type ListDto } from '@wishare/shared';
import { api, ApiError } from '../lib/api.js';
import { formatRelativeDays, pluralize } from '../lib/format.js';
import { ListMosaic } from '../components/ListMosaic.js';
import { ItemForm, type ItemFormValues, type PrefilledCard } from '../components/ItemForm.js';
import { взятьКарточкуИзХранилища } from './AddFromPagePage.js';
import { Button, EmptyState, Modal, Notice, SelectField, TextAreaField, TextField } from '../components/ui.js';
import { SkeletonLists } from '../components/Skeleton.js';
import { IconGift, IconSparkle } from '../components/icons.js';

/**
 * Главный экран: мои списки желаний.
 *
 * Первое, что видит новый пользователь, — не пустая таблица, а объяснение, что
 * делать. Пустое состояние здесь важнее, чем заполненное: именно оно решает,
 * останется человек в продукте или закроет вкладку.
 */

export function HomePage(): JSX.Element {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  // Ссылка, прилетевшая из другого приложения через «поделиться».
  const [sharedUrl, setSharedUrl] = useState<string | null>(null);
  // Готовая карточка от закладки-кнопки: её разбирать заново не нужно.
  const [prefilledCard, setPrefilledCard] = useState<PrefilledCard | null>(null);

  useEffect(() => {
    // Ссылка приходит либо в состоянии перехода (человек уже был внутри),
    // либо в sessionStorage (пережила вход). Проверяем оба источника и сразу
    // подчищаем — иначе форма всплывёт снова при следующем заходе на главную.
    const fromState = (location.state as { sharedUrl?: string } | null)?.sharedUrl;
    let fromStorage: string | null = null;
    try {
      fromStorage = sessionStorage.getItem('wishare:shared-url');
      if (fromStorage) sessionStorage.removeItem('wishare:shared-url');
    } catch {
      /* хранилище недоступно — ничего страшного */
    }

    const карточка =
      (location.state as { prefilledCard?: PrefilledCard } | null)?.prefilledCard ?? взятьКарточкуИзХранилища();
    if (карточка) {
      setPrefilledCard(карточка);
      if ((location.state as { prefilledCard?: PrefilledCard } | null)?.prefilledCard) {
        navigate('/', { replace: true, state: null });
      }
      return;
    }

    const incoming = fromState ?? fromStorage;
    if (incoming) {
      setSharedUrl(incoming);
      // Убираем ссылку из состояния истории, чтобы обновление страницы
      // не открывало форму заново.
      if (fromState) navigate('/', { replace: true, state: null });
    }
  }, [location.state, navigate]);

  const { data: lists, isLoading, error, refetch } = useQuery({
    queryKey: ['lists', 'mine'],
    queryFn: api.lists.mine,
  });

  const createList = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.lists.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lists', 'mine'] });
      setCreating(false);
      // Карточка, отложенная ради создания списка, дожидается своего часа
      // здесь: список появился, и её снова есть куда положить.
    },
  });

  /*
   * Шапка не зависит от данных, поэтому написана ОДИН раз и показывается в
   * обоих состояниях — и пока грузится, и когда пришло.
   *
   * Раньше страница на время загрузки становилась пустой с кружком посередине,
   * а потом целиком перерисовывалась: заголовок, кнопка и сетка появлялись
   * разом и сдвигали друг друга. Теперь на месте стоит то, что и так известно,
   * меняются только карточки. Кнопка «Новый список» при этом рабочая: создание
   * списка загрузки не ждёт.
   */
  const шапка = (
    <div className="page-header">
      <div>
        <h1>Мои списки</h1>
        <p className="muted small">Здесь всё, что вы хотите. Друзья увидят это, а брони — нет.</p>
      </div>
      <Button variant="primary" onClick={() => setCreating(true)}>
        + Новый список
      </Button>
    </div>
  );

  /*
   * Окно создания списка тоже написано один раз и живёт в обоих состояниях:
   * иначе кнопка в шапке во время загрузки нажималась бы вхолостую — окна,
   * которое она открывает, на странице ещё нет.
   */
  const окноСоздания = (
    <CreateListModal
      open={creating}
      onClose={() => setCreating(false)}
      onCreate={(input) => createList.mutateAsync(input)}
      error={createList.error instanceof ApiError ? createList.error.message : null}
    />
  );

  if (isLoading) {
    return (
      <>
        {шапка}
        <SkeletonLists />
        {окноСоздания}
      </>
    );
  }

  if (error) {
    /*
     * Кнопка тут не украшение. Самая частая причина этой ошибки — оборванная
     * связь в телефоне, и лечится она одним повтором. Без кнопки человеку
     * остаётся закрыть и открыть приложение заново — то есть сделать руками
     * ровно то, что оно умеет само.
     */
    return (
      <Notice tone="danger">
        <div className="stack stack--sm">
          <span>{error instanceof ApiError ? error.message : 'Не удалось загрузить списки'}</span>
          <div>
            <Button onClick={() => void refetch()}>Попробовать снова</Button>
          </div>
        </div>
      </Notice>
    );
  }

  const active = (lists ?? []).filter((list) => !list.isArchived);
  const archived = (lists ?? []).filter((list) => list.isArchived);

  return (
    <>
      {шапка}

      {active.length === 0 ? (
        <EmptyState
          icon={<IconSparkle />}
          title="Пока пусто — и это легко исправить"
          text="Создайте список, вставьте в него ссылку на что угодно, и мы сами найдём фотографию и цену. Друзья смогут забронировать подарок так, что вы об этом не узнаете."
          action={
            <Button variant="primary" size="lg" onClick={() => setCreating(true)}>
              Создать первый список
            </Button>
          }
        />
      ) : (
        <div className="lists-grid">
          {active.map((list) => (
            <ListRow key={list.id} list={list} />
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <details style={{ marginTop: 'var(--space-6)' }}>
          <summary className="muted small" style={{ cursor: 'pointer' }}>
            В архиве: {pluralize(archived.length, 'список', 'списка', 'списков')}
          </summary>
          <div className="lists-grid" style={{ marginTop: 'var(--space-3)' }}>
            {archived.map((list) => (
              <ListRow key={list.id} list={list} />
            ))}
          </div>
        </details>
      )}

      {окноСоздания}

      {/*
        Пока человек создаёт список, окно с карточкой прячем: два окна разом
        — это уже не помощь, а неразбериха. Карточка при этом жива и вернётся
        сама, как только список появится.
      */}
      {(sharedUrl || prefilledCard) && !creating && (
        <QuickAddModal
          url={prefilledCard?.url ?? sharedUrl ?? ''}
          card={prefilledCard}
          lists={active}
          onClose={() => { setSharedUrl(null); setPrefilledCard(null); }}
          onNeedList={() => {
            /*
             * Карточку НЕ выбрасываем, уходя создавать список.
             *
             * Это первый опыт нового человека: он поставил кнопку, нажал её
             * на товаре — и попал сюда, потому что списка у него ещё нет.
             * Потерять здесь собранную карточку значит отправить его искать
             * тот же товар заново. Вернём её сразу, как список появится.
             */
            setSharedUrl(null);
            setCreating(true);
          }}
          onAdded={() => {
            setSharedUrl(null);
            setPrefilledCard(null);
            void queryClient.invalidateQueries({ queryKey: ['lists', 'mine'] });
          }}
        />
      )}
    </>
  );
}

/**
 * Быстрое добавление желания по ссылке, которой поделились из другого приложения.
 *
 * Отличается от обычного добавления одним: желание может лечь в любой из списков,
 * поэтому сверху стоит выбор списка. Если список всего один, выбор не показываем —
 * лишний шаг там, где решение очевидно.
 */
interface QuickAddModalProps {
  url: string;
  /** Карточка, собранная закладкой на самой странице магазина. */
  card?: PrefilledCard | null;
  lists: ListDto[];
  onClose: () => void;
  onNeedList: () => void;
  onAdded: () => void;
}

function QuickAddModal({ url, card, lists, onClose, onNeedList, onAdded }: QuickAddModalProps): JSX.Element {
  const [targetListId, setTargetListId] = useState<string>(lists[0]?.id ?? '');

  // Списков нет вовсе — добавлять некуда. Предлагаем сначала завести список.
  if (lists.length === 0) {
    return (
      <Modal open title="Сначала нужен список" onClose={onClose}>
        <div className="stack">
          <Notice tone="info" icon={<IconGift size={19} />}>
            Чтобы сохранить желание, создайте список — например «Хочу когда-нибудь».
          </Notice>
          <p className="small muted truncate">Ссылка: {url}</p>
          <div className="row">
            <Button variant="ghost" onClick={onClose} block>
              Позже
            </Button>
            <Button variant="primary" onClick={onNeedList} block>
              Создать список
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  const create = async (values: ItemFormValues): Promise<void> => {
    await api.items.create(targetListId, toItemPayload(values));
    onAdded();
  };

  return (
    <Modal open title={card ? 'Новое желание со страницы' : 'Новое желание по ссылке'} onClose={onClose}>
      <div className="stack">
        {lists.length > 1 && (
          <SelectField
            label="В какой список"
            value={targetListId}
            onChange={setTargetListId}
            options={lists.map((list) => ({
              value: list.id,
              label: `${list.coverEmoji || '🎁'} ${list.title}`,
            }))}
          />
        )}
        <ItemForm initialUrl={url} initialCard={card} onSubmit={create} onCancel={onClose} />
      </div>
    </Modal>
  );
}

/** Приводит значения формы к телу запроса создания желания. */
function toItemPayload(values: ItemFormValues): Record<string, unknown> {
  return {
    title: values.title,
    description: values.description || undefined,
    url: values.url,
    imageUrl: values.imageUrl,
    imageCandidates: values.imageCandidates,
    priceMinor: values.priceMinor,
    currency: values.currency,
    priority: values.priority,
    quantity: values.quantity,
    groupGift: values.groupGift,
    sourceSite: values.sourceSite,
  };
}

/**
 * Список на главной.
 *
 * Раньше это была строка во всю ширину — она честно сообщала название и число
 * желаний, но первый экран после входа выглядел как страница настроек.
 * Карточка занимает столько же места, зато обложка списка становится видна
 * сразу, а праздник, до которого осталось меньше месяца, подсвечен: за этим
 * сюда и заходят.
 */
function ListRow({ list }: { list: ListDto }): JSX.Element {
  const скоро = list.eventDate ? formatRelativeDays(list.eventDate) : null;

  return (
    <Link to={`/lists/${list.id}`} className="list-card">
      {/*
        Мозаика из первых желаний вместо одного смайлика.

        Карточка списка показывала смайлик, название и число желаний — и три
        списка выглядели тремя одинаковыми прямоугольниками: чтобы вспомнить,
        что в каком лежит, приходилось открывать каждый. Теперь список узнаётся
        по своему содержимому, как пластинка по обложке. Смайлик остаётся —
        он выбран человеком и работает как метка, — но уже поверх мозаики.
      */}
      <ListMosaic list={list} />

      <span className="list-card__title truncate">{list.title}</span>

      <span className="list-card__count">
        {pluralize(list.itemCount, 'желание', 'желания', 'желаний')}
      </span>

      <span className="list-card__meta">
        <span className="badge">{LIST_VISIBILITY_LABEL[list.visibility]}</span>
        {скоро && <span className="badge badge--warning">{скоро}</span>}
      </span>
    </Link>
  );
}

interface CreateListModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: Record<string, unknown>) => Promise<unknown>;
  error: string | null;
}

function CreateListModal({ open, onClose, onCreate, error }: CreateListModalProps): JSX.Element {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<string>('friends');
  const [eventDate, setEventDate] = useState('');
  const [emoji, setEmoji] = useState('🎁');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!title.trim()) return;

    setBusy(true);
    try {
      await onCreate({
        title: title.trim(),
        description: description.trim() || undefined,
        visibility,
        eventDate: eventDate || null,
        coverEmoji: emoji || null,
      });
      setTitle('');
      setDescription('');
      setEventDate('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} title="Новый список" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div style={{ width: 78 }}>
            <TextField label="Значок" value={emoji} onChange={(event) => setEmoji(event.target.value.slice(0, 4))} />
          </div>
          <div className="grow">
            <TextField
              label="Название"
              placeholder="День рождения, Новый год, Хочу когда-нибудь"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              autoFocus
              required
            />
          </div>
        </div>

        <SelectField
          label="Кто увидит"
          value={visibility}
          onChange={setVisibility}
          hint={
            visibility === 'friends'
              ? 'Только подтверждённые друзья.'
              : visibility === 'link'
                ? 'Любой, у кого есть секретная ссылка. Удобно для дальних родственников.'
                : visibility === 'private'
                  ? 'Никто, кроме вас. Можно копить идеи молча.'
                  : 'Любой вошедший пользователь, кто откроет ваш профиль.'
          }
          options={LIST_VISIBILITIES.map((value) => ({ value, label: LIST_VISIBILITY_LABEL[value] }))}
        />

        <TextField
          label="Дата события"
          type="date"
          value={eventDate}
          onChange={(event) => setEventDate(event.target.value)}
          hint="Необязательно. Поможет друзьям не опоздать."
        />

        <TextAreaField
          label="Описание"
          placeholder="Пара слов для друзей"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={2}
        />

        {error && (
          <Notice tone="danger">
            {error}
          </Notice>
        )}

        <div className="row">
          <Button variant="ghost" type="button" onClick={onClose} block>
            Отмена
          </Button>
          <Button variant="primary" type="submit" loading={busy} disabled={!title.trim()} block>
            Создать
          </Button>
        </div>
      </form>
    </Modal>
  );
}
